import PersonOutlineTwoToneIcon from '@mui/icons-material/PersonOutlineTwoTone';
import { PermissionContext, Permissions } from '@lanterds/types';
import { FmtpPage } from 'src/components/common/NavigationMenu/types';
import { PageNames } from './types';

export const MediaQueries = {
    Tablet: { query: '(min-width: 401px) and (max-width: 800px)' },
    Phone: { query: '(max-width: 400px)' },
};

export const SupportEmail = 'support@lanterds.com';
export const DRAWER_WIDTH = 240;
export const IS_SIDE_NAVIGATION_OPEN = 'sideNavOpen';
export const SELECTED_SHIPPING_PORTAL_TYPE_LOCAL_STORAGE_KEY = 'fmtpShippingType';
export const GENERAL_ERROR_TITLE = 'Whoops';
export const GENERAL_ERROR_ICON = 'sentiment_very_dissatisfied';
export const GENERAL_ERROR_MESSAGE = 'An error has occurred; please try again later.';
export const GENERIC_NETWORK_ERROR_MESSAGE = 'Network response was not ok';
export const NETWORK_CONNECTION_ERROR_ICON = 'cloud_off';
export const CONTACT_SUPPORT_MESSAGE = 'If you continue to have issues, notify IT Support at: ';
export const PERMISSIONS_ERROR_ICON = 'security';
export const PERMISSIONS_ERROR_TITLE = 'Permission Error';
export const NO_PERMISSIONS_ERROR_MESSAGE = 'You do not have any permissions assigned to your user account.';
export const MISSING_PERMISSION_ERROR_MESSAGE = 'You do not have permission to view this page.';
export const NO_PERMISSIONS_ERROR_CONTACT_SUPPORT_MESSAGE =
    'Please contact your account administrator or notify IT Support at: ';
export const SESSION_ID_QUERY_PARAM = 'sessionId';

export const PAGE_INFO: Record<string, FmtpPage> = {
    shippingPortal: {
        href: '/shipping-portal',
        name: PageNames.ShippingPortal,
        displayText: 'Labels',
        permissions: [
            Permissions.shippingPortal_returns,
            Permissions.shippingPortal_transfers,
            Permissions.shippingPortal_outbound,
        ],
        subPages: [
            {
                href: '/shipping-portal?type=transfer',
                name: PageNames.Transfer,
                displayText: 'Stock Transfers',
                permissions: [Permissions.shippingPortal_transfers],
            },
            {
                href: '/shipping-portal?type=return',
                name: PageNames.Return,
                displayText: 'Material Returns',
                permissions: [Permissions.shippingPortal_returns],
            },
            {
                href: '/shipping-portal?type=outbound',
                name: PageNames.Outbound,
                displayText: 'Outbound',
                permissions: [Permissions.shippingPortal_outbound],
            },
        ],
    },
    parcelSearch: {
        href: '/',
        name: PageNames.ParcelSearch,
        displayText: 'Parcel Search',
        permissions: [Permissions.parcelSearch],
        subPages: [
            {
                href: '/parcel',
                name: PageNames.ParcelDetail,
                displayText: 'Parcel Details',
                doNotDisplay: true,
            },
        ],
    },
    parcelSearchV1: {
        href: '/v1/parcelSearch',
        name: PageNames.ParcelSearch,
        displayText: 'Parcel Search',
        permissions: [Permissions.parcelSearch],
    },
    userManagement: {
        href: '/admin/users',
        name: PageNames.UserManagement,
        displayText: 'Users',
        permissions: [Permissions.userManagement],
        subPages: [
            {
                href: '/admin/users',
                name: PageNames.Users,
                icon: PersonOutlineTwoToneIcon,
                displayText: 'Users',
                doNotDisplay: true,
            },
            {
                href: '/admin/user',
                name: PageNames.User,
                icon: PersonOutlineTwoToneIcon,
                displayText: 'User',
                doNotDisplay: true,
            },
        ],
    },
    reports: {
        href: '/reports',
        name: PageNames.Reports,
        displayText: 'Reports',
        permissions: [
            Permissions.reports_transferReport,
            Permissions.reports_returnsReport,
            Permissions.reports_otdReport,
            Permissions.reports_eltMetricsReport,
            Permissions.reports_plsReportAllSources,
            Permissions.reports_volumeReportAllSources,
            Permissions.reports_billing,
            Permissions.reports_tenderExceptionReport,
            Permissions.reports_distressedParcels,
            Permissions.reports_fmtpActivityReport,
            Permissions.reports_assetTracking,
            Permissions.reports_scanDetail,
            Permissions.reports_osdmReport,
            Permissions.reports_osd_manifest,
        ],
    },
    shipmentTracker: {
        href: '/shipment-tracker/stop-details',
        name: PageNames.ShipmentTracker,
        displayText: 'Shipments',
        permissions: [Permissions.shipmentTracking],
    },
    deliveryManagement: {
        href: '/delivery-management',
        name: PageNames.DeliveryManagement,
        displayText: 'DMT',
        permissions: [Permissions.deliveryManagementTool],
    },
};

export const PermissionDisplayNames: Record<Permissions, string> = {
    [Permissions.userManagement]: 'User Management',
    [Permissions.userManagement_deleteUser]: 'User Management - Delete User',
    [Permissions.parcelSearch]: 'Parcel Search',
    [Permissions.parcelSearch_extendedSearch]: 'Parcel Search - Extended Search',
    [Permissions.parcelSearch_deliveryPictures]: 'Parcel Search - Delivery Pictures',
    [Permissions.parcelSearch_deliveryPicturesIncludeAll]: 'Parcel Search - Delivery Pictures (Include All)',
    [Permissions.shippingPortal_returns]: 'Labels - Returns',
    [Permissions.shippingPortal_transfers]: 'Labels - Transfers',
    [Permissions.shippingPortal_outbound]: 'Labels - Outbound',
    [Permissions.shippingPortal_unfilteredDestinations]: 'Labels - Unfiltered Destinations',
    [Permissions.reports_transferReport]: 'Reports - Transfer Report',
    [Permissions.reports_returnsReport]: 'Reports - Returns Report',
    [Permissions.reports_otdReport]: 'Reports - OTD',
    [Permissions.reports_eltMetricsReport]: 'Reports - ELT Metrics Report',
    [Permissions.reports_plsReportAllSources]: 'Reports - PLS Report',
    [Permissions.reports_volumeReportAllSources]: 'Reports - Volume Report',
    [Permissions.reports_billing]: 'Reports - Billing',
    [Permissions.reports_tenderExceptionReport]: 'Reports - Tender Exception Report',
    [Permissions.reports_distressedParcels]: 'Reports - Distressed Parcels',
    [Permissions.reports_fmtpActivityReport]: 'Reports - FMTP Activity Report',
    [Permissions.reports_assetTracking]: 'Reports - Asset Tracking',
    [Permissions.reports_scanDetail]: 'Reports - Scan Detail',
    [Permissions.reports_osd_manifest]: 'Reports - OS&D Manifest Report',
    [Permissions.reports_osdmReport]: 'Reports - O/S/D/M Report',
    [Permissions.reports_returns]: 'Reports - Returns Report Sigma',
    [Permissions.reports_transfers]: 'Reports - Transfer Report Sigma',
    [Permissions.shipmentTracking]: 'Shipment Tracker',
    [Permissions.deliveryManagementTool]: 'DMT',
    [Permissions.exportData]: 'Export Data',
};

export const PermissionContextDisplayName: Record<PermissionContext, string> = {
    [PermissionContext.GLOBAL]: 'Global',
    [PermissionContext.CUSTOMER]: 'Customer',
    [PermissionContext.DEALER]: 'Dealer',
};

export const allCustomersDatum = {
    customerId: 'All',
    customerName: 'All Customers',
};

export const allCustomersValue = {
    id: allCustomersDatum.customerId,
    label: allCustomersDatum.customerName,
};

export const ParcelSearchErrors = {
    startDateOrParcelNum: 'Select a start date or enter a parcel number to search',
    genericError: `Please try again. If you continue to have issues notify IT Support at ${SupportEmail}`,
    noEndDate: 'If no end date is provided, start date should be no more than 7 days in the past',
    periodTooLong: 'The difference between the start and end dates must be no more than 7 days',
    startDateTooOld: 'Please enter a start date no more than 7 days from the end date',
    endDateTooOld: 'Please enter an end date no more than 7 days from the start date',
    requiredLength: 'Please enter at least 4 characters',
    invalidStartDate: 'Start date cannot be after the end date',
    parcelOrManifestNum: 'Please enter a parcel or manifest number',
    invalidCharacter: 'Invalid character "%" entered',
};
